import { isPlanAvailableForTraderStatus, isPlanSelectableForTraderStatus } from './rules';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverResult } from '@tanstack/query-core';
import type { SubscriptionPlan } from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getPlansQuery } from 'src/queries/subscriptions';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

type Plan = z.infer<typeof SubscriptionPlan>;

interface SubscriptionPlanCatalogModelInit {
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    isProfessionalTrader: ReadonlySignal<boolean>;
    showInviteCodeEntry: ReadonlySignal<boolean>;
    includePlan?: (plan: Plan) => boolean;
}

function mergePlans(basePlans: readonly Plan[], invitePlans: readonly Plan[]): Plan[] {
    const planMap = new Map<string, Plan>();
    for (const plan of basePlans) {
        planMap.set(plan.id, plan);
    }
    for (const plan of invitePlans) {
        planMap.set(plan.id, plan);
    }

    const replacedPlanIds = new Set<string>();
    for (const plan of planMap.values()) {
        for (const replacedId of plan.replacesPlanIds ?? []) {
            replacedPlanIds.add(replacedId);
        }
    }

    return Array.from(planMap.values())
        .filter((plan) => !replacedPlanIds.has(plan.id))
        .sort((a, b) => {
            if (a.inviteCodeRequired && !b.inviteCodeRequired) return -1;
            if (!a.inviteCodeRequired && b.inviteCodeRequired) return 1;
            return 0;
        });
}

export const SubscriptionPlanCatalogModel = createModel((init: SubscriptionPlanCatalogModelInit) => {
    const basePlans = signal<Plan[]>([]);
    const invitePlans = signal<Plan[]>([]);
    const inviteCode = signal('');

    const basePlansQuery = init.queryObserverModelFactory<Plan[], Error, Plan[]>(getPlansQuery());
    const inviteCodePlansQuery = init.queryObserverModelFactory<Plan[], Error, Plan[]>(
        computed(() => {
            const trimmedInviteCode = init.showInviteCodeEntry.value ? inviteCode.value.trim() : '';
            return {
                ...getPlansQuery({
                    inviteCode: trimmedInviteCode || undefined,
                }),
                enabled: Boolean(trimmedInviteCode) && init.showInviteCodeEntry.value,
                retry: false,
            };
        }),
    );

    effect(() => {
        const subscription = basePlansQuery.subscribe((result: QueryObserverResult<Plan[], Error>) => {
            if (result.status === 'success') {
                basePlans.value = result.data ?? [];
            }
        });
        return () => subscription[Symbol.dispose]();
    });

    effect(() => {
        const subscription = inviteCodePlansQuery.subscribe((result: QueryObserverResult<Plan[], Error>) => {
            if (result.status === 'success') {
                invitePlans.value = result.data ?? [];
            } else if (result.error) {
                invitePlans.value = [];
            }
        });
        return () => subscription[Symbol.dispose]();
    });

    effect(() => {
        if (init.showInviteCodeEntry.value && inviteCode.value.trim()) return;
        if (invitePlans.value.length === 0) return;
        invitePlans.value = [];
    });

    effect(() => {
        return () => {
            basePlansQuery[Symbol.dispose]();
            inviteCodePlansQuery[Symbol.dispose]();
        };
    });

    const plans: ReadonlySignal<Plan[]> = computed(() => mergePlans(basePlans.value, invitePlans.value));

    const availablePlans: ReadonlySignal<Plan[]> = computed(() =>
        plans.value.filter(
            (plan) =>
                isPlanAvailableForTraderStatus(plan, init.isProfessionalTrader.value) &&
                (init.includePlan?.(plan) ?? true),
        ),
    );

    const plansLoading: ReadonlySignal<boolean> = computed(
        () =>
            plans.value.length === 0 &&
            ((basePlansQuery.signal.value.isFetching ?? false) ||
                (inviteCodePlansQuery.signal.value.isFetching ?? false)),
    );

    const isInviteCodeValid: ReadonlySignal<boolean | null> = computed(() => {
        if (!init.showInviteCodeEntry.value) return null;

        const trimmedInviteCode = inviteCode.value.trim();
        if (!trimmedInviteCode) return null;

        const queryState = inviteCodePlansQuery.signal.value;
        if (queryState.error || queryState.status !== 'success') return null;

        return (
            queryState.data?.some(
                (plan) =>
                    plan.inviteCodeRequired === true &&
                    isPlanAvailableForTraderStatus(plan, init.isProfessionalTrader.value) &&
                    (init.includePlan?.(plan) ?? true),
            ) ?? false
        );
    });

    const inviteCodePlan: ReadonlySignal<Plan | null> = computed(() => {
        if (isInviteCodeValid.value !== true) return null;

        return (
            availablePlans.value.find(
                (plan) =>
                    plan.inviteCodeRequired === true &&
                    isPlanSelectableForTraderStatus(plan, init.isProfessionalTrader.value),
            ) ?? null
        );
    });

    function setInviteCode(nextInviteCode: string): void {
        inviteCode.value = nextInviteCode;
    }

    function resetInviteCode(): void {
        inviteCode.value = '';
        invitePlans.value = [];
    }

    async function fetchPlans(): Promise<QueryObserverResult<Plan[], Error>> {
        const trimmedInviteCode = init.showInviteCodeEntry.value ? inviteCode.value.trim() : '';
        if (trimmedInviteCode) {
            return inviteCodePlansQuery.queryObserver.refetch();
        }
        if (invitePlans.value.length > 0) {
            invitePlans.value = [];
        }
        return basePlansQuery.queryObserver.refetch();
    }

    function getInviteCodePlansQuery(): QueryObserverModel<Plan[], Error, Plan[]> {
        return inviteCodePlansQuery;
    }

    function getBasePlansQuery(): QueryObserverModel<Plan[], Error, Plan[]> {
        return basePlansQuery;
    }

    return {
        plans,
        availablePlans,
        plansLoading,
        inviteCode,
        isInviteCodeValid,
        inviteCodePlan,
        setInviteCode,
        resetInviteCode,
        fetchPlans,
        getBasePlansQuery,
        getInviteCodePlansQuery,
    };
});

export type SubscriptionPlanCatalogModel = InstanceType<typeof SubscriptionPlanCatalogModel>;
